import { t as __exportAll } from "./chunk-rolldown-runtime.mjs";
import { n as addSpanProcessor } from "./chunk-span-fanout.mjs";
import { t as storage_exports } from "./chunk-storage.mjs";
import { t as stubExtension } from "./chunk-stub.mjs";
import { t as RemoteMetricsForwarder } from "./chunk-remote-metrics.mjs";
import { t as activeTraceContext } from "./chunk-trace-context.mjs";
import { r as posthog_exports } from "./chunk-ObservabilityExtension.mjs";
import { LogLevel, log } from "@dxos/log";
import { isNode, isNonNullable } from "@dxos/util";
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from "@opentelemetry/semantic-conventions";
import { defaultResource, resourceFromAttributes } from "@opentelemetry/resources";
import * as Effect from "effect/Effect";
import { getEnvString, resolveTelemetryTag } from "@dxos/config";
import * as Match from "effect/Match";
import * as Option from "effect/Option";
import * as Ref from "effect/Ref";
var cli_observability_secrets_default = {
	POSTHOG_API_KEY: "phc_G8oXhAk9fw9kevIE8XGiYySvbMujJntU4anYimHdnUl",
	IPDATA_API_KEY: "73dfdecdf979c18f07d50cf841bbdd9e589f237256326ac8cca23786",
	OTEL_ENDPOINT: "https://composer.space/api/otel",
	OTEL_HEADERS: null
};
//#endregion
//#region src/extensions/otel/extension.ts
var __dxlog_file = "/__w/dxos/dxos/packages/sdk/observability/src/extensions/otel/extension.ts";
/** Create an OTEL-backed observability extension for logs, metrics, and/or traces. */
var extensions = Effect.fn(function* ({ serviceName, serviceVersion, environment, namespace = serviceName, config, endpoint: _endpoint, headers: _headers, additionalDestinations = [], logs: logsEnabled = false, logLevel = LogLevel.INFO, observabilityWorker, metrics: metricsEnabled = false, traces: tracesEnabled = false }) {
	const { OtelLogs } = yield* Effect.promise(() => import("./chunk-logs.mjs").then((n) => n.r));
	const { OtelMetrics } = yield* Effect.promise(() => import("./chunk-metrics.mjs").then((n) => n.n));
	const { OtelTraces } = yield* Effect.promise(() => import("#otel-traces"));
	const disabled = (yield* Effect.promise(() => (0, storage_exports.isObservabilityDisabled)(namespace))) || isObservabilityDisabledSync(namespace);
	const storedLogLevel = yield* Effect.promise(() => (0, storage_exports.getOtelLogLevel)(namespace));
	const resolvedLogLevel = storedLogLevel != null ? LogLevel[storedLogLevel.toUpperCase()] ?? logLevel : logLevel;
	const enabledRef = yield* Ref.make(!disabled);
	const tags = /* @__PURE__ */ new Map();
	const logTags = /* @__PURE__ */ new Map();
	const rawEndpoint = isNode() ? process.env.DX_OTEL_ENDPOINT ?? _endpoint ?? cli_observability_secrets_default.OTEL_ENDPOINT : getEnvString(config, "DX_OTEL_ENDPOINT") ?? _endpoint;
	const endpoint = !isNode() && rawEndpoint?.startsWith("/") ? resolveRelativeEndpoint(rawEndpoint) : rawEndpoint;
	const headers = _headers ?? Match.value(isNode()).pipe(Match.when(true, () => Option.fromNullishOr(process.env.DX_OTEL_HEADERS ?? cli_observability_secrets_default.OTEL_HEADERS)), Match.when(false, () => Option.fromNullishOr(getEnvString(config, "DX_OTEL_HEADERS"))), Match.exhaustive, Option.map((raw) => parseHeaders(raw)), Option.getOrElse(() => void 0));
	if (!endpoint) log.info("Missing OTEL_ENDPOINT", void 0, {
		"~LogMeta": "~LogMeta",
		F: __dxlog_file,
		L: 110,
		S: this
	});
	const destinations = [...endpoint ? [{
		endpoint,
		headers: headers ?? {}
	}] : [], ...additionalDestinations];
	if (destinations.length === 0) return stubExtension;
	log.info("otel destinations", { destinations: destinations.map(({ endpoint }) => endpoint) }, {
		"~LogMeta": "~LogMeta",
		F: __dxlog_file,
		L: 120,
		S: this
	});
	const clientTag = resolveTelemetryTag(config);
	if (clientTag) tags.set("ctx.tag", clientTag);
	const commitHash = config.get("runtime.app.build.commitHash");
	const baseAttributes = {
		[ATTR_SERVICE_NAME]: serviceName,
		[ATTR_SERVICE_VERSION]: serviceVersion,
		"deployment.environment": environment,
		"dxos.process.type": detectProcessType(),
		...clientTag ? { "ctx.tag": clientTag } : {},
		...commitHash ? { "vcs.ref.head.revision": commitHash } : {}
	};
	const sessionId = crypto.randomUUID();
	const { resource, metricsResource } = createResources(baseAttributes, sessionId);
	const traces = tracesEnabled && !disabled ? new OtelTraces({
		destinations,
		resource,
		getTags: () => Object.fromEntries(tags),
		spanSink: observabilityWorker ? { post: (record) => observabilityWorker.post(record) } : void 0
	}) : void 0;
	const remoteLogs = logsEnabled && !disabled ? observabilityWorker : void 0;
	const logs = logsEnabled && !disabled && !remoteLogs ? new OtelLogs({
		destinations,
		resource,
		getTags: () => ({
			...Object.fromEntries(tags),
			...Object.fromEntries(logTags)
		}),
		logLevel: resolvedLogLevel,
		onTraceFlagged: (traceId) => traces?.promote(traceId)
	}) : void 0;
	const remoteMetrics = metricsEnabled && observabilityWorker && !disabled ? new RemoteMetricsForwarder(observabilityWorker.post) : void 0;
	const metrics = metricsEnabled && !disabled && !observabilityWorker ? new OtelMetrics({
		destinations,
		resource: metricsResource,
		getTags: () => Object.fromEntries(tags)
	}) : void 0;
	const extension = {
		initialize: () => Effect.sync(() => {
			if (logs) log.runtimeConfig.processors.push(logs.logProcessor);
			if (remoteLogs) remoteLogs.post({
				type: "otel-init",
				destinations,
				resourceAttributes: {
					...baseAttributes,
					"session.id": sessionId
				},
				logLevel: resolvedLogLevel,
				tags: Object.fromEntries(tags)
			});
			if (remoteMetrics) observabilityWorker?.post({
				type: "otel-metrics-init",
				destinations,
				resourceAttributes: baseAttributes,
				tags: Object.fromEntries(tags)
			});
			if (traces) {
				if (observabilityWorker) observabilityWorker.post({
					type: "otel-traces-init",
					destinations,
					resourceAttributes: {
						...baseAttributes,
						"session.id": sessionId
					}
				});
				traces.start();
			}
		}),
		enable: Effect.fn(function* () {
			yield* Effect.promise(() => (0, storage_exports.storeObservabilityDisabled)(namespace, false));
			yield* Ref.update(enabledRef, () => true);
		}),
		disable: Effect.fn(function* () {
			yield* Ref.update(enabledRef, () => false);
			if (logs) {
				const index = log.runtimeConfig.processors.indexOf(logs.logProcessor);
				if (index >= 0) log.runtimeConfig.processors.splice(index, 1);
			}
			yield* Effect.promise(() => (0, storage_exports.storeObservabilityDisabled)(namespace, true));
			yield* extension.close();
		}),
		close: () => Effect.promise(async () => {
			remoteMetrics?.flush();
			remoteLogs?.post({ type: "otel-flush" });
			const results = await Promise.allSettled([
				logs?.close(),
				metrics?.close(),
				remoteMetrics?.close()
			]);
			for (const result of results) if (result.status === "rejected") log.catch(result.reason, void 0, {
				"~LogMeta": "~LogMeta",
				F: __dxlog_file,
				L: 256,
				S: this
			});
			await traces?.close();
		}),
		flush: () => Effect.promise(async () => {
			remoteMetrics?.flush();
			remoteLogs?.post({ type: "otel-flush" });
			const results = await Promise.allSettled([logs?.flush(), metrics?.flush()]);
			for (const result of results) if (result.status === "rejected") log.catch(result.reason, void 0, {
				"~LogMeta": "~LogMeta",
				F: __dxlog_file,
				L: 271,
				S: this
			});
			await traces?.flush();
		}),
		setTags: (incomingTags, kind) => {
			const target = kind === "logs" ? logTags : tags;
			for (const [key, value] of Object.entries(incomingTags)) target.set(key, value);
			remoteLogs?.post({
				type: "otel-tags",
				tags: { ...incomingTags }
			});
		},
		get enabled() {
			return Ref.get(enabledRef).pipe(Effect.runSync);
		},
		apis: [
			{
				kind: "logs",
				isAvailable: () => Effect.succeed(!!logs || !!remoteLogs)
			},
			metricsApi(metrics ?? remoteMetrics),
			traces ? {
				kind: "traces",
				isAvailable: () => Effect.succeed(true)
			} : void 0
		].filter(isNonNullable)
	};
	return extension;
});
var metricsApi = (metrics) => metrics ? {
	kind: "metrics",
	isAvailable: () => Effect.succeed(true),
	gauge: (name, value, tags, meta) => metrics.gauge(name, value, tags, meta),
	increment: (name, value, tags, meta) => metrics.increment(name, value, tags, meta),
	distribution: (name, value, tags, meta) => metrics.distribution(name, value, tags, meta),
	observe: (name, callback, tags, meta) => metrics.observe(name, callback, tags, meta)
} : void 0;
/**
* Builds the resource for logs/traces and the separate one for metrics.
* Metrics omit `session.id` because a per-page-load attribute mints a new time series on every
* reload. A separate resource is the only option: views reach datapoint attributes, not resource ones.
*/
var createResources = (attributes, sessionId) => ({
	resource: defaultResource().merge(resourceFromAttributes({
		...attributes,
		"session.id": sessionId
	})),
	metricsResource: defaultResource().merge(resourceFromAttributes(attributes))
});
/**
* Synchronous best-effort check for observability opt-out.
* Prevents telemetry from being emitted before the async storage check completes.
*/
var isObservabilityDisabledSync = (serviceName) => {
	if (isNode()) return process.env.DX_DISABLE_OBSERVABILITY === "true";
	try {
		if (typeof localStorage !== "undefined") return localStorage.getItem(`${serviceName}/observability-disabled`) === "true";
	} catch {}
	return false;
};
/** Best-effort detection of the JavaScript execution context type. */
var detectProcessType = () => {
	if (isNode()) return "node";
	if (typeof window !== "undefined") return "browser";
	if (typeof globalThis.ServiceWorkerGlobalScope !== "undefined") return "service-worker";
	if (typeof globalThis.SharedWorkerGlobalScope !== "undefined") return "shared-worker";
	return "dedicated-worker";
};
/**
* Resolves a relative OTLP endpoint path against the current global origin.
* Workers expose the same `globalThis.location` as the page that spawned them;
* if neither is available we fall back to the original path and let the OTLP
* exporter surface the configuration error.
*/
var resolveRelativeEndpoint = (path) => {
	const origin = globalThis.location?.origin;
	return origin ? `${origin}${path}` : path;
};
var parseHeaders = (unparsedHeaders) => {
	return unparsedHeaders.split(";").reduce((acc, header) => {
		const [key, ...rest] = header.split(":");
		if (key && rest.length > 0) acc[key.trim().toLowerCase()] = rest.join(":").trim();
		return acc;
	}, {});
};
//#endregion
//#region src/extensions/otel/index.ts
var otel_exports = /* @__PURE__ */ __exportAll({
	activeTraceContext: () => activeTraceContext,
	addSpanProcessor: () => addSpanProcessor,
	createResources: () => createResources,
	extensions: () => extensions
});
//#endregion
//#region src/extensions/index.ts
var extensions_exports = /* @__PURE__ */ __exportAll({
	Otel: () => otel_exports,
	PostHog: () => posthog_exports
});
//#endregion
export { otel_exports as n, cli_observability_secrets_default as r, extensions_exports as t };

//# sourceMappingURL=chunk-extensions.mjs.map